import { useEffect, useMemo, useRef } from "react";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "#/components/atoms/sidebar";
import { Separator } from "#/components/atoms/separator";
import { AppSidebar } from "#/components/app-sidebar";
import { LegacyAccessButton } from "#/components/molecules/legacy-access-button";
import { RouteBreadcrumb } from "#/components/route-breadcrumb";
import { useMeContext } from "#/contexts/MeContext";
import { TenantContextProvider } from "#/contexts/TenantContext";
import { MarketProvider } from "#/contexts/MarketContext";
import { setTenantId } from "#/lib/api/client";

export const Route = createFileRoute("/$tenantCode/$market")({
  component: TenantMarketLayout,
});

function TenantMarketLayout() {
  const { tenantCode, market } = Route.useParams();
  const navigate = useNavigate();
  const { meData } = useMeContext();
  const queryClient = useQueryClient();
  const prevTenantIdRef = useRef<number | undefined>(undefined);

  const tenant = meData.tenants.find((t) => t.code === tenantCode);

  // Redirect to root if tenant code is invalid
  useEffect(() => {
    if (tenant === undefined) {
      void navigate({ to: "/", replace: true });
    }
  }, [tenant, navigate]);

  // Sync tenant to API client middleware
  useEffect(() => {
    if (tenant !== undefined) {
      setTenantId(tenant.id);
      if (
        prevTenantIdRef.current !== undefined &&
        prevTenantIdRef.current !== tenant.id
      ) {
        void queryClient.invalidateQueries();
      }
      prevTenantIdRef.current = tenant.id;
    }
  }, [tenant, queryClient]);

  const tenantContextValue = useMemo(() => {
    if (tenant === undefined) return undefined;
    return {
      tenant,
      permissions: meData.permissions[String(tenant.id)] ?? [],
      isAdmin: meData.isAdmin[String(tenant.id)] ?? false,
    };
  }, [tenant, meData.permissions, meData.isAdmin]);

  if (tenant === undefined || tenantContextValue === undefined) return null;

  return (
    <TenantContextProvider value={tenantContextValue}>
      <MarketProvider market={market}>
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset>
            <header className="flex justify-between h-16 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 print:hidden">
              <div className="-ml-1 flex items-center gap-2">
                <SidebarTrigger />
                <Separator
                  orientation="vertical"
                  className="mr-2 self-center! h-4"
                />
                <RouteBreadcrumb />
              </div>
              <LegacyAccessButton />
            </header>
            <main className="flex-1">
              <Outlet />
            </main>
          </SidebarInset>
        </SidebarProvider>
      </MarketProvider>
    </TenantContextProvider>
  );
}
